
import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Edit, 
  Plus, 
  Search, 
  Trash, 
  Loader2,
  AlertCircle 
} from "lucide-react";
import { api } from "@/lib/api";
import { FormDialog } from "@/components/admin/FormDialog";
import { ProductForm, ProductFormValues } from "@/components/admin/ProductForm";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ProductsAdmin = () => {
  const [search, setSearch] = useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<any>(null);
  const [deletingProductId, setDeletingProductId] = useState<string | null>(null);
  
  const queryClient = useQueryClient();
  
  const { 
    data: products = [], 
    isLoading, 
    isError 
  } = useQuery({
    queryKey: ['products'],
    queryFn: api.products.getAll
  });
  
  const filteredProducts = products.filter((product: any) =>
    product.name.toLowerCase().includes(search.toLowerCase()) ||
    product.category.toLowerCase().includes(search.toLowerCase())
  );

  const handleCreateProduct = async (values: ProductFormValues) => {
    await api.products.create(values);
    setIsCreateDialogOpen(false);
    queryClient.invalidateQueries({ queryKey: ['products'] });
  };

  const handleUpdateProduct = async (values: ProductFormValues) => {
    if (editingProduct) {
      await api.products.update(editingProduct.id, values);
      setEditingProduct(null);
      queryClient.invalidateQueries({ queryKey: ['products'] });
    }
  };

  const handleDeleteProduct = async () => {
    if (deletingProductId) {
      await api.products.delete(deletingProductId);
      setDeletingProductId(null);
      queryClient.invalidateQueries({ queryKey: ['products'] });
    }
  };

  // Create wrapper functions that match the FormDialog's expected signature
  const handleCreateSubmit = async () => {
    // The actual form submission is handled by ProductForm
  };

  const handleUpdateSubmit = async () => {
    // The actual form submission is handled by ProductForm
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Products</h1>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Product
        </Button>
      </div>
      
      {/* Search field */}
      <div className="relative w-full max-w-sm">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Search products..."
          className="pl-8"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      
      {/* Products table */}
      {isLoading ? (
        <div className="flex justify-center items-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : isError ? (
        <div className="flex justify-center items-center py-8 text-destructive">
          <AlertCircle className="h-8 w-8 mr-2" />
          <p>Error loading products. Please try again.</p>
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProducts.length > 0 ? (
                filteredProducts.map((product: any) => (
                  <TableRow key={product.id}>
                    <TableCell>{product.name}</TableCell>
                    <TableCell>{product.category}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        product.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {product.status === 'active' ? 'Active' : 'Draft'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => setEditingProduct(product)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => setDeletingProductId(product.id)}
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                    No products found. Try adjusting your search or create a new product.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
      
      {/* Create Product Dialog */}
      <FormDialog
        title="Create Product"
        description="Add a new product to your catalog"
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSubmit={handleCreateSubmit}
      >
        <ProductForm onSubmit={handleCreateProduct} />
      </FormDialog>
      
      {/* Edit Product Dialog */}
      <FormDialog
        title="Edit Product"
        description="Update product information"
        isOpen={!!editingProduct}
        onClose={() => setEditingProduct(null)}
        onSubmit={handleUpdateSubmit}
      >
        {editingProduct && (
          <ProductForm 
            defaultValues={editingProduct} 
            onSubmit={handleUpdateProduct} 
          />
        )}
      </FormDialog>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingProductId} onOpenChange={() => setDeletingProductId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the product.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteProduct}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProductsAdmin;
